import React from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { PromoRewardNotice, MeetingRoomRewardPanel } from './PromoRewardNotice';
import { PaymentMethodSelector } from './PaymentMethodSelector';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { format, addDays } from 'date-fns';
import {
  ArrowLeft, Building2, MapPin, CreditCard, Loader2, ShieldAlert,
  Calendar as CalendarIcon, Tag, X
} from 'lucide-react';

const round2 = (n) => Math.round(n * 100) / 100;

export const BookingSummary = ({
  selectedVenueData, selectedBookableData,
  selectedDate, selectedDates = null, startTime, endTime,
  attendees, setAttendees, notes, setNotes,
  isInternal, setIsInternal, bookedFor, setBookedFor,
  useCredits,
  useDayPass, dayPassBalance, dayPassApplicable,
  paymentMethod, onPaymentMethodChange, creditQuote, creditQuoteLoading,
  calculateTotal, bookingResult,
  user, isAuthenticated, loading,
  couponCode, setCouponCode, appliedCoupon, couponBusy,
  onApplyCoupon, onRemoveCoupon,
  promoReward = null, meetingRoomReward = null,
  onBack, onConfirm
}) => {
  const dayPassWillApply = !isInternal && useDayPass;
  // Coupon % is applied to the full bill (base + add-ons + GST), not just the
  // pre-GST base — the backend's discounted taxable/GST split already nets out
  // to this same total, so this only needs to match it for display purposes.
  const grossTotal = calculateTotal().total;
  const couponDiscountAmount = appliedCoupon
    ? Math.min(grossTotal, round2(grossTotal * (appliedCoupon.discount_percentage / 100)))
    : 0;
  const totalAfterCoupon = round2(grossTotal - couponDiscountAmount);
  return (
  <div className="space-y-6 animate-fade-in">
    <div className="flex items-center justify-between">
      <div>
        <h2 className="font-heading text-2xl font-bold mb-2">Booking Details</h2>
        <p className="text-muted-foreground">Review and confirm your booking</p>
      </div>
      <Button variant="ghost" onClick={onBack}>
        <ArrowLeft className="mr-2 w-4 h-4" /> Back
      </Button>
    </div>

    <div className="grid md:grid-cols-2 gap-6">
      {/* Booking Summary */}
      <Card className="bg-card border-border">
        <CardHeader><CardTitle className="text-lg">Booking Summary</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start space-x-3">
            <Building2 className="w-5 h-5 text-primary mt-0.5" />
            <div>
              <p className="font-medium">{selectedVenueData?.name}</p>
              <p className="text-sm text-muted-foreground">{selectedVenueData?.address}</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <MapPin className="w-5 h-5 text-primary mt-0.5" />
            <div>
              <p className="font-medium">{selectedBookableData?.name}</p>
              <p className="text-sm text-muted-foreground">Capacity: {selectedBookableData?.capacity} people</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <CalendarIcon className="w-5 h-5 text-primary mt-0.5" />
            <div>
              {selectedBookableData?.booking_mode === 'monthly' ? (
                <>
                  <p className="font-medium">Membership Period</p>
                  <p className="text-sm text-muted-foreground">
                    {selectedDate && format(selectedDate, 'PPP')} &rarr; {selectedDate && format(addDays(selectedDate, 31), 'PPP')} (1 month)
                  </p>
                </>
              ) : selectedDates ? (
                <>
                  <p className="font-medium">{selectedDates.length} dates · {calculateTotal().totalPasses} Day Passes</p>
                  <ul className="text-sm text-muted-foreground" data-testid="summary-dates">
                    {selectedDates.map(d => (
                      <li key={d.getTime()}>{format(d, 'EEE, d MMM yyyy')} · {calculateTotal().passQuantity} pass{calculateTotal().passQuantity !== 1 ? 'es' : ''}</li>
                    ))}
                  </ul>
                  <p className="text-sm text-muted-foreground mt-1">{startTime} - {endTime} each day · one payment, one bill</p>
                </>
              ) : (
                <>
                  <p className="font-medium">{selectedDate && format(selectedDate, 'PPP')}</p>
                  <p className="text-sm text-muted-foreground">{startTime} - {endTime} ({calculateTotal().hours} hours)</p>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Additional Details */}
      <Card className="bg-card border-border">
        <CardHeader><CardTitle className="text-lg">Additional Details</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Number of Attendees</Label>
            <Input type="number" min={1} max={selectedBookableData?.capacity || 10} value={attendees}
              onChange={(e) => setAttendees(parseInt(e.target.value) || 1)} className="bg-input/50" data-testid="attendees-input" />
          </div>
          <div className="space-y-2">
            <Label>Notes (Optional)</Label>
            <Textarea placeholder="Any special requirements..." value={notes}
              onChange={(e) => setNotes(e.target.value)} className="bg-input/50" data-testid="notes-input" />
          </div>

          {['center_manager', 'cluster_manager', 'hub_manager', 'admin'].includes(user?.role) && (
            <div className="space-y-3 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg">
              <div className="flex items-center space-x-2">
                <Checkbox id="isInternal" checked={isInternal}
                  onCheckedChange={(checked) => setIsInternal(checked)}
                  data-testid="internal-booking-checkbox" />
                <Label htmlFor="isInternal" className="text-sm cursor-pointer font-medium">
                  Book for ClayWorks (Internal Use) — No Payment Required
                </Label>
              </div>
              {isInternal && (
                <div className="space-y-2 pl-6">
                  <Label className="text-sm">Who is this meeting room for?</Label>
                  <Input placeholder="e.g., Board Meeting, Team Standup, Client Visit..."
                    value={bookedFor} onChange={(e) => setBookedFor(e.target.value)}
                    className="bg-input/50" data-testid="booked-for-input" />
                </div>
              )}
            </div>
          )}

        </CardContent>
      </Card>
    </div>

    {/* Price Breakdown */}
    <Card className="bg-card border-border">
      <CardHeader>
        <CardTitle className="text-lg flex items-center">
          <CreditCard className="w-5 h-5 mr-2 text-primary" /> Price Breakdown
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {isInternal ? (
            <div className="flex justify-between text-lg font-semibold py-2">
              <span>Internal Booking</span>
              <span className="text-green-600">No Charge</span>
            </div>
          ) : (
            <>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{calculateTotal().baseLabel}</span>
                <span>₹{calculateTotal().baseAmount.toFixed(2)}</span>
              </div>
              {calculateTotal().addOnBreakdown?.map((a, i) => (
                <div key={i} className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{a.name} ({a.detail})</span>
                  <span>₹{a.amount.toFixed(2)}</span>
                </div>
              ))}
              {selectedBookableData?.gst_applicable && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">GST ({calculateTotal().gstRate}%)</span>
                  <span>₹{calculateTotal().gstAmount.toFixed(2)}</span>
                </div>
              )}
              {appliedCoupon && (
                <div className="flex justify-between text-sm text-emerald-600">
                  <span>Coupon ({appliedCoupon.code} · {appliedCoupon.discount_percentage}%)</span>
                  <span>- ₹{couponDiscountAmount.toFixed(2)}</span>
                </div>
              )}
              {dayPassWillApply && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Day Pass{(calculateTotal().passQuantity || 1) !== 1 ? 'es' : ''} to Use</span>
                  <span>{calculateTotal().passQuantity || 1} pass{(calculateTotal().passQuantity || 1) !== 1 ? 'es' : ''}</span>
                </div>
              )}
              {useCredits && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Credits to Deduct</span>
                  <span>{creditQuote?.credits_required != null ? Number(creditQuote.credits_required).toFixed(1) : '...'} credits</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-semibold pt-2 border-t border-border">
                <span>Total</span>
                <span className="text-primary" data-testid="checkout-total-amount">
                  {dayPassWillApply
                    ? '₹0 (via Day Pass)'
                    : useCredits
                      ? '₹0 (via Credits)'
                      : appliedCoupon
                        ? `₹${totalAfterCoupon.toFixed(2)}`
                        : `₹${calculateTotal().total.toFixed(2)}`}
                </span>
              </div>
            </>
          )}
        </div>
        {!isInternal && (
          <>
            <PromoRewardNotice reward={promoReward} payingWithBalance={dayPassWillApply || useCredits} />
            <MeetingRoomRewardPanel
              reward={meetingRoomReward}
              eligible={!!user && user.role === 'client' && !user.is_guest}
              payingWithBalance={useCredits}
            />
          </>
        )}
      </CardContent>
    </Card>

    {!isInternal && isAuthenticated && onPaymentMethodChange && (
      <PaymentMethodSelector
        paymentMethod={paymentMethod} onChange={onPaymentMethodChange}
        moneyTotal={appliedCoupon ? totalAfterCoupon : grossTotal}
        creditQuote={creditQuote} creditQuoteLoading={creditQuoteLoading}
        dayPassApplicable={dayPassApplicable} dayPassBalance={dayPassBalance}
        passQuantity={calculateTotal().passQuantity || 1}
        promoReward={promoReward} meetingRoomReward={meetingRoomReward}
        moneyOnlyNote={selectedDates
          ? 'Several dates are paid together by card or UPI, with one bill. To use credits or your Day Pass balance, book one date at a time.'
          : null}
      />
    )}

    {/* Coupon code (self-service external bookings only — not for internal) */}
    {!isInternal && setCouponCode && (
      <Card className="bg-card border-border" data-testid="coupon-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center">
            <Tag className="w-5 h-5 mr-2 text-primary" /> Have a Coupon Code?
          </CardTitle>
        </CardHeader>
        <CardContent>
          {appliedCoupon ? (
            <div className="flex items-center justify-between p-3 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800">
              <div>
                <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">
                  Coupon Applied Successfully
                </p>
                <p className="text-xs text-emerald-600/80 mt-1">
                  {appliedCoupon.code} applied · {appliedCoupon.discount_percentage}% Discount Added · You save ₹{couponDiscountAmount.toFixed(2)}
                </p>
                {appliedCoupon.description && (
                  <p className="text-[11px] text-emerald-600/70 mt-1">{appliedCoupon.description}</p>
                )}
              </div>
              <Button variant="ghost" size="sm" onClick={onRemoveCoupon} data-testid="coupon-remove-btn">
                <X className="w-4 h-4 mr-1" /> Remove
              </Button>
            </div>
          ) : (
            <div className="flex gap-2">
              <Input
                placeholder="Enter coupon code (e.g. CLAY20)"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase().replace(/\s/g, ''))}
                className="uppercase"
                data-testid="coupon-input"
              />
              <Button onClick={onApplyCoupon} disabled={couponBusy} data-testid="coupon-apply-btn">
                {couponBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    )}

    {/* Cancellation Policy */}
    {!isInternal && (
      <Card className="bg-card border-border" data-testid="cancellation-policy-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center">
            <ShieldAlert className="w-5 h-5 mr-2 text-primary" /> Cancellation Policy
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {[
              { time: '48+ hours before', refund: '100%', color: 'text-emerald-600', bg: 'bg-emerald-500/10' },
              { time: '24-48 hours before', refund: '75%', color: 'text-emerald-500', bg: 'bg-emerald-500/5' },
              { time: '12-24 hours before', refund: '50%', color: 'text-amber-500', bg: 'bg-amber-500/5' },
              { time: '6-12 hours before', refund: '25%', color: 'text-orange-500', bg: 'bg-orange-500/5' },
              { time: 'Less than 6 hours', refund: 'No refund', color: 'text-red-500', bg: 'bg-red-500/5' },
            ].map((tier, i) => (
              <div key={i} className={`flex items-center justify-between py-2 px-3 rounded-lg ${tier.bg}`}>
                <span className="text-sm text-muted-foreground">{tier.time}</span>
                <span className={`text-sm font-semibold ${tier.color}`}>{tier.refund} refund</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            Refund timelines are calculated from the start time of the booking. Credits-based bookings follow the same policy.
          </p>
        </CardContent>
      </Card>
    )}

    <div className="flex justify-end">
      <Button onClick={onConfirm} disabled={loading} className="bg-primary hover:bg-primary/90" data-testid="confirm-booking-btn">
        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
        {isAuthenticated ? 'Confirm Booking' : 'Login to Book'}
      </Button>
    </div>
  </div>
  );
};
